import {
	type FanShopProductType,
	type FetchFanShopProductTypesData,
	type FetchFanShopProductTypesParams,
	minutesToMs
} from '@capsicum/common';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { validationRule } from '../../util';
import { fanShopApi } from '../api';

export const UseProductTypesQueryKeyBuilder = (params?: FetchFanShopProductTypesParams) =>
	[
		'fanShop',
		'productTypes',
		...(params?.['partnerIds[]'] ?? []),
		...(params?.['productRedeemTypes[]'] ?? [])
	] as const;

export const useProductTypesQuery = (
	params?: FetchFanShopProductTypesParams,
	options?: Omit<UseQueryOptions<FetchFanShopProductTypesData, unknown, Array<FanShopProductType>>, 'queryFn'>
) => {
	const isAllPartnersIdValid =
		params &&
		params['partnerIds[]'] &&
		params['partnerIds[]'].length > 0 &&
		params['partnerIds[]']?.every(validationRule);

	const partnerIds: Partial<Pick<FetchFanShopProductTypesParams, 'partnerIds[]'>> = isAllPartnersIdValid
		? { 'partnerIds[]': params?.['partnerIds[]'] }
		: {};

	const hasProductRedeemTypes = params && params['productRedeemTypes[]'] && params['productRedeemTypes[]'].length > 0;

	const productRedeemTypes: Partial<Pick<FetchFanShopProductTypesParams, 'productRedeemTypes[]'>> =
		hasProductRedeemTypes ? { 'productRedeemTypes[]': params?.['productRedeemTypes[]'] } : {};

	return useQuery<FetchFanShopProductTypesData, unknown, Array<FanShopProductType>>({
		queryFn: async () =>
			(await fanShopApi).fetchFanShopProductTypes({
				hasProducts: true,
				...partnerIds,
				...productRedeemTypes
			} as FetchFanShopProductTypesParams),
		queryKey: UseProductTypesQueryKeyBuilder({ ...partnerIds, ...productRedeemTypes }),
		select: (data: FetchFanShopProductTypesData) => data.data || [],
		staleTime: minutesToMs(5),
		...options
	});
};
